import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { COLORS } from '@cuemath/comps';

import { ReactComponent as SearchIcon } from 'img/search.svg';

const NewSearchWrapper = styled.div`
  width: ${({ width }) => width || '327px'};
  height: 44px;
  position: relative;
  border-radius: 8px;
`;

const NewSearchBar = styled.input`
  width: ${({ width }) => width || '327px'};
  height: 44px;
  position: relative;
  border: 1px solid ${COLORS.GRAY_XLIGHT};
  border-radius: 8px;
  padding: 10px 36px 10px 16px;
  color: ${COLORS.COLA_LIGHTER};
  outline: none;
  &:focus {
    border: 1px solid ${COLORS.GRAY};
    transition: 0.35s ease;
    color: ${COLORS.COLA};
    &::-webkit-input-placeholder {
      transition: opacity 0.45s ease;
      opacity: 0;
    }
    &::-moz-placeholder {
      transition: opacity 0.45s ease;
      opacity: 0;
    }
    &:-ms-placeholder {
      transition: opacity 0.45s ease;
      opacity: 0;
    }
  }

  ${({ disableSteps }) =>
    disableSteps &&
    css`
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        margin: 0;
      }
    `}

  input[type='number'] {
    appearance: textfield;
    -moz-appearance: textfield;
  }
`;

const SearchImg = styled(SearchIcon)`
  width: 24px;
  height: 24px;
  position: absolute;
  right: 0;
  top: 50%;
  bottom: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  transition: all 0.2s ease;
`;

const ClearIcon = styled.div`
  width: 24px;
  height: 24px;
  position: absolute;
  right: 0;
  top: 50%;
  bottom: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${COLORS.GRAY_LIGHT};
  color: ${COLORS.GRAY_DARK};
  font-size: 16px;
  font-weight: bold;
  transition: all 0.2s ease;

  &:hover {
    background: ${COLORS.GRAY};
    color: ${COLORS.WHITE};
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

export default function Search({
  placeholder,
  disabled,
  type,
  disableSteps,
  onSearch,
  showNewSearchBar,
  width,
  customSearchData,
  showClearIcon = false,
}) {
  const { onChange, searchValue: customSearchValue } = customSearchData || {};
  const noKeyPressSearch =
    typeof onChange === 'function' && typeof customSearchValue === 'string';
  const [searchValue, setSearchValue] = useState('');

  const onKeyPress = event => {
    const { value: searchKey } = (event && event.target) || {};

    if (event.key === 'Enter' && (showNewSearchBar || searchKey !== ''))
      onSearch(searchKey);
  };

  const onSearchBarChange = e => {
    const { value } = e?.target || {};

    if (noKeyPressSearch) {
      onChange(value);
    } else {
      setSearchValue(value);
    }
  };

  const handleSearchClick = () => {
    const valueToSearch = noKeyPressSearch ? customSearchValue : searchValue;
    onSearch(valueToSearch);
  };

  const handleClearClick = () => {
    onSearch('');
  };

  return (
    <NewSearchWrapper width={width}>
      <NewSearchBar
        onKeyPress={onKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        type={type}
        disableSteps={disableSteps}
        value={noKeyPressSearch ? customSearchValue : searchValue}
        onChange={onSearchBarChange}
        width={width}
      />
      {showClearIcon ? (
        <ClearIcon onClick={handleClearClick} title="Clear search">
          ×
        </ClearIcon>
      ) : (
        <SearchImg onClick={handleSearchClick} />
      )}
    </NewSearchWrapper>
  );
}

Search.propTypes = {
  disableSteps: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  onSearch: PropTypes.func,
  showNewSearchBar: PropTypes.bool,
  customSearchData: PropTypes.shape({}),
  showClearIcon: PropTypes.bool,
};

Search.defaultProps = {
  disableSteps: false,
  disabled: false,
  placeholder: '',
  type: 'text',
  onSearch: () => {},
  onChange: () => {},
  showNewSearchBar: false,
  customSearchData: null,
  showClearIcon: false,
};
